import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Edit, Trash2, X, Save } from "lucide-react";
import { Button } from "../../components/UI/Button";
import TextInput from "../../components/UI/TextInput";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { contentUseCase } from "../../../di/container";
import { useAuthStore } from "../../stores/auth-store";
import type { Notice, NoticeCreateRequest } from "../../../domain/types/content";

/**
 * 운영 종료 공지 기본 틀.
 *
 * 직접 쓰기 번거로운 경우 그대로 두고 날짜와 문의처만 채우면 되도록 구성했다.
 * 종료 신청 화면에서 넘어올 때만 채워 넣는다.
 */
const CLOSING_NOTICE_TEMPLATE = {
  title: "[안내] 사이트 운영 종료 안내",
  content: `안녕하세요, 고객님.
그동안 저희 서비스를 이용해 주셔서 감사합니다.

부득이한 사정으로 아래와 같이 사이트 운영을 종료하게 되어 안내드립니다.

  · 운영 종료일 : YYYY년 MM월 DD일
  · 신규 주문 접수 마감 : YYYY년 MM월 DD일
  · 문의처 : (연락처 / 이메일을 입력해 주세요)

종료일 이전에 접수된 주문은 정상적으로 제작·배송됩니다.
필요하신 주문 내역과 견적서는 종료일 전에 미리 내려받아 주시기 바랍니다.

그동안 이용해 주셔서 진심으로 감사합니다.`,
};

type ViewMode = "list" | "detail" | "edit" | "create";

const NoticeManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  // 운영 종료 신청 화면에서 넘어오면 목록이 아니라 종료 공지 작성 화면으로 바로 들어온다.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const closingMode = searchParams.get("mode") === "closing";

  // 폼 상태
  const [formData, setFormData] = useState<NoticeCreateRequest>({
    title: "",
    content: "",
    isPublished: true,
    noticeType: "GENERAL",
  });

  useEffect(() => {
    if (!closingMode) return;

    setSelectedNotice(null);
    setFormData({
      title: CLOSING_NOTICE_TEMPLATE.title,
      content: CLOSING_NOTICE_TEMPLATE.content,
      isPublished: true,
      noticeType: "CLOSING",
    });
    setViewMode("create");
  }, [closingMode]);

  // 데이터 로드
  useEffect(() => {
    if (user?.companyId) {
      loadNotices();
    }
  }, [user?.companyId]);

  const loadNotices = async () => {
    if (!user?.companyId) return;

    setLoading(true);
    try {
      const data = await contentUseCase.getNotices(user.companyId);
      setNotices(data);
    } catch (error) {
      console.error('공지사항 로드 실패:', error);
      alert('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 검색 필터링
  const filteredNotices = useMemo(() => {
    return notices.filter(
      (notice) =>
        notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notice.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notice.author.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [notices, searchQuery]);

  // 공지사항 선택 (상세보기)
  const handleSelectNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setViewMode("detail");
  };

  // 공지사항 수정 모드
  const handleEditNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      isPublished: notice.isPublished,
      noticeType: notice.noticeType ?? "GENERAL",
    });
    setViewMode("edit");
  };

  // 공지사항 삭제
  const handleDeleteNotice = async (noticeId: number) => {
    if (!window.confirm("정말 삭제하시겠습니까?") || !user?.companyId) return;

    try {
      await contentUseCase.deleteNotice(user.companyId, noticeId);
      await loadNotices();
      setViewMode("list");
      setSelectedNotice(null);
      alert('공지사항이 삭제되었습니다.');
    } catch (error) {
      console.error('공지사항 삭제 실패:', error);
      alert('공지사항 삭제에 실패했습니다.');
    }
  };

  // 공지사항 생성 모드
  const handleCreateNotice = () => {
    setFormData({
      title: "",
      content: "",
      isPublished: true,
      noticeType: "GENERAL",
    });
    setSelectedNotice(null);
    setViewMode("create");
  };

  // 저장 (생성/수정)
  const handleSave = async () => {
    if (!user?.companyId) return;

    // 종료 공지는 이용자에게 알리는 것이 목적이라 비공개로 저장될 일이 없다.
    const payload: NoticeCreateRequest =
      formData.noticeType === "CLOSING" ? { ...formData, isPublished: true } : formData;

    try {
      if (viewMode === "create") {
        const newNotice = await contentUseCase.createNotice(user.companyId, payload);
        await loadNotices();
        setSelectedNotice(newNotice);
        setViewMode("detail");
        alert('공지사항이 생성되었습니다.');
      } else if (viewMode === "edit" && selectedNotice) {
        const updatedNotice = await contentUseCase.updateNotice(
          user.companyId,
          selectedNotice.id,
          payload
        );
        await loadNotices();
        setSelectedNotice(updatedNotice);
        setViewMode("detail");
        alert('공지사항이 수정되었습니다.');
      }
    } catch (error) {
      console.error('공지사항 저장 실패:', error);
      alert('공지사항 저장에 실패했습니다.');
    }
  };

  // 취소
  const handleCancel = () => {
    // 종료 절차 안에서 들어온 화면이라 목록이 아니라 왔던 곳으로 돌려보낸다.
    if (closingMode) {
      navigate("/profile/unregister");
      return;
    }

    if (selectedNotice) {
      setViewMode("detail");
    } else {
      setViewMode("list");
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="flex h-full bg-gray-50 overflow-hidden">
        {/* 좌측 목록 */}
        <div className="w-full lg:w-96 bg-white border-r border-gray-200 flex flex-col h-full">
          {/* 헤더 */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold text-gray-900">공지사항 관리</h1>
              <Button onClick={handleCreateNotice} className="flex items-center gap-1 text-sm">
                <Plus className="w-4 h-4" />
                작성
              </Button>
            </div>
            {/* 검색 */}
            <div className="relative">
             <TextInput
                type="text"
                placeholder="검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-sm"
                inputClassName="pr-6"
              />
              <Search className="absolute z-1 right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* 목록 */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            {filteredNotices.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                공지사항이 없습니다.
              </div>
            ) : (
              filteredNotices.map((notice) => (
                <div
                  key={notice.id}
                  onClick={() => handleSelectNotice(notice)}
                  className={`py-4 px-6 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedNotice?.id === notice.id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-1">
                      {notice.title}
                    </h3>
                    {notice.noticeType === "CLOSING" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#FEF2F2] text-[#F24A4A] ml-2 flex-shrink-0">
                        운영 종료
                      </span>
                    )}
                    {notice.isPublished ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-2 flex-shrink-0">
                        공개
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-2 flex-shrink-0">
                        비공개
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2 text-left">
                    {notice.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{notice.createdAt}</span>
                    <span>조회 {notice.views}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 우측 상세/편집 영역 */}
        <div className={`${viewMode === "list" ? "hidden" : ""} h-100vh overflow-y-scroll lg:block absolute top-[116px] bg-white py-[25px] lg:top-auto left-[0px] md:left-[199px] lg:left-auto right-0 lg:right-auto bottom-0 lg:bottom-auto lg:relative  lg:bg-auto lg:flex-1 lg:overflow-y-auto lg:overflow-x-hidden lg:h-full`}>
          {viewMode === "list" && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-gray-300 mb-4 flex justify-center">
                  <svg className="w-24 h-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">공지사항을 선택하세요</h2>
                <p className="text-sm text-gray-500">좌측 목록에서 공지사항을 선택하거나 새로 작성하세요</p>
              </div>
            </div>
          )}

          {viewMode === "detail" && selectedNotice && (
            <div className="px-8 w-full">
              <div className="w-full">
                {/* 헤더 */}
                <div className="flex items-start md:items-center flex-col md:flex-row gap-3 justify-between mb-6">
                  <div className="flex flex-row gap-3 items-center">
                    <div role="button" onClick={() => {setViewMode("list");setSelectedNotice(null);}}>
                      <svg
                        width="8"
                        height="12"
                        viewBox="0 0 8 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.12095 0.170067C7.32709 0.376209 7.34583 0.698787 7.17717 0.926099L7.12095 0.991223L2.11243 6L7.12095 11.0088C7.32709 11.2149 7.34583 11.5375 7.17717 11.7648L7.12095 11.8299C6.91481 12.0361 6.59223 12.0548 6.36492 11.8862L6.29979 11.8299L0.880438 6.41058C0.674296 6.20444 0.655556 5.88186 0.824217 5.65455L0.880438 5.58942L6.29979 0.170067C6.52655 -0.056689 6.89419 -0.056689 7.12095 0.170067Z"
                          fill="#252525"
                        />
                      </svg>
                    </div>
                    <h2 className="text-2xl text-start font-bold text-gray-900">{selectedNotice.title}</h2>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleEditNotice(selectedNotice)}
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      수정
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDeleteNotice(selectedNotice.id)}
                      className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      삭제
                    </Button>
                  </div>
                </div>

                {/* 메타 정보 */}
                <div className="rounded-lg mb-6">
                  <div className="flex flex-col xl:flex-row items-start xl:items-center gap-2 xl:gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">작성자:</span>
                      <span className="ml-2 text-gray-900">{selectedNotice.author}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">작성일:</span>
                      <span className="ml-2 text-gray-900">{selectedNotice.createdAt}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">공개여부:</span>
                      <span>
                        {selectedNotice.isPublished ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            공개
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            비공개
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 내용 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="prose max-w-none">
                    {/* 본문의 개행을 살린다. HTML 은 \n 을 공백으로 취급해 그냥 두면 한 줄로 붙는다. */}
                    <p className="text-gray-700 break-words text-left whitespace-pre-wrap">
                      {selectedNotice.content}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(viewMode === "edit" || viewMode === "create") && (
            <div className="w-full px-8">
              <div className="w-full">
                {/* 헤더 */}
                <div className="flex items-center justify-between mb-6">
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {closingMode
                        ? "사이트 운영 종료 알리기"
                        : viewMode === "create"
                          ? "공지사항 작성"
                          : "공지사항 수정"}
                    </h2>
                    {closingMode && (
                      <p className="text-sm text-gray-500 mt-1">
                        운영 종료 절차의 첫 단계입니다. 이용자에게 종료를 알리는 공지를 게시합니다.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      {closingMode ? "그만두기" : "취소"}
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!formData.title || !formData.content}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {closingMode ? "공지 게시하기" : "저장"}
                    </Button>
                  </div>
                </div>

                {/*
                  일반 공지 작성과 생김새가 같으면 잘못 눌러 들어온 사람이 알아채기 어렵다.
                  무엇을 하는 화면인지와 빠져나갈 길을 먼저 보여 준다.
                */}
                {closingMode && (
                  <div className="rounded-lg border border-[#F24A4A]/20 bg-[#FEF2F2] px-5 py-4 mb-6 text-left">
                    <p className="text-sm font-bold text-[#F24A4A] mb-1">
                      이 공지는 쇼핑몰 운영을 종료하기 위한 공지입니다.
                    </p>
                    <p className="text-[13px] text-gray-600 leading-relaxed">
                      게시하면 이용자에게 종료 예정이 안내되고, 게시 후 1개월이 지나야 운영 종료를
                      신청할 수 있습니다. 공지만 게시한다고 사이트가 닫히지는 않습니다.
                      <br />
                      일반 공지를 쓰려는 것이었다면{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            title: "",
                            content: "",
                            isPublished: true,
                            noticeType: "GENERAL",
                          });
                          navigate(`/company/${user?.companyId}/contents/notices`);
                        }}
                        className="underline text-[#4285F4] hover:text-[#3367D6]"
                      >
                        일반 공지 작성으로 이동
                      </button>
                      해 주세요.
                    </p>
                  </div>
                )}
          
                {/* 폼 */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      제목 <span className="text-red-500">*</span>
                    </label>
                    <TextInput
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="제목을 입력하세요"
                      className="text-lg"
                    />
                  </div>
          
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      내용 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({ ...formData, content: e.target.value })
                      }
                      placeholder="내용을 입력하세요"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[400px] resize-y"
                    />
                  </div>
          
                  {/*
                    종료 공지는 이용자에게 알리는 것이 목적이라 비공개로 둘 이유가 없다.
                    종료 신청에서 넘어온 경우에는 선택지를 없애고 무엇으로 저장되는지만 알려 준다.
                  */}
                  {closingMode || formData.noticeType === "CLOSING" ? (
                    <div className="rounded-lg bg-gray-50 px-4 py-3 text-left">
                      <p className="text-xs text-gray-500">
                        사이트 운영 종료 공지로 공개 등록됩니다. 게시 후 1개월이 지나야 운영 종료를
                        신청할 수 있습니다.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        공개 여부
                      </label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, isPublished: !formData.isPublished })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            formData.isPublished ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.isPublished ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className="ml-3 text-sm text-gray-700">
                          {formData.isPublished ? '공개' : '비공개'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CompanyAdminLayout>
  );
};

export default NoticeManagement;
